/**
 * What B0 knows about the world: the country table that seeds the norm pick, the company
 * languages a firm can pick from, and the two helpers that read a norm out of the catalogue.
 *
 * **THIS FILE NO LONGER OWNS THE NORM ENUM, AND THAT IS THE POINT OF `KLQ-20260827-001`.**
 * It used to — a `NORM_PRESETS` list of six free strings whose TWIN comment said, correctly
 * for its time, that "the wire field is a free string, so the *enum lives here*". That
 * sentence stopped being true the day the catalogue shipped: `norm` is a seeded table now,
 * its codes are `STN` · `ČSN` · `DIN` · `ÖNORM` · `EN-ISO`, and the claim resolves whatever
 * it is handed against them. Two of the six values this file used to send — `EN-ISO baseline`
 * and `Custom` — name no row and would have been refused with a `422` at the end of a
 * two-step wizard. So the list is gone, the TWIN with it, and the wizard renders
 * `GET /api/v1/norms` instead (`api.ts:readNormCatalogue`, `queries.ts:useNormCatalogue`).
 *
 * What stays here is the thing the server cannot answer for a person who has not chosen yet:
 * **which norm a country starts on**, and the ten countries the mock draws.
 *
 * The country table is the mock's, not an invention: `design/project/screens/workspace-setup.jsx`
 * draws exactly these ten in exactly these two groups, and the `…meta` lines are the ones it
 * prints under each name.
 *
 * **A country seeds; it never decides.** Picking Slovakia writes `sk` and ticks `STN`, and
 * both controls below stay live — MCK-20260521-010 is explicit that the pick "auto-suggests
 * locale, language and norm preset; all editable". A Slovak practice working in German is a
 * real firm, not an edge case, and since this spec a Slovak practice that also takes Czech
 * work ticks `ČSN` beside `STN` rather than choosing between them.
 */

import type { MessageKeys } from '@/lib/i18n';

import type { Norm, NormVersion } from './api';

type SetupKey = MessageKeys['setup'];

/**
 * The four codes the country table seeds, as `norm.code` spells them.
 *
 * These are *seeds*, not an enum: the full list of what a studio may pick comes from the
 * catalogue, and a code added there needs no line here unless some country should start on
 * it. TWIN: `_BY_COUNTRY` in `backend/src/kalqa/domains/workspaces/service.py`, which answers
 * the same question for a claim that sent no norm at all. The two must agree, or a wizard
 * that sends no norm lands somewhere else than one that names the country's.
 *
 * **A third copy since MCK-20260521-010 phase 1:** `LocaleDefaults.norm_code` in
 * `backend/src/kalqa/core/locale_defaults.py`, which pairs each country's norm with the date
 * format, decimal separator, paper series and units that country expects — served as
 * reference data by `GET /api/v1/locale-defaults`. It carries its own copy because
 * `kalqa.core` is the bottom import layer and may not reach into a domain. All three are held
 * together by `backend/tests/domains/test_locale_defaults.py`, not by these comments.
 */
export const NORM_STN = 'STN';
export const NORM_CSN = 'ČSN';
export const NORM_DIN = 'DIN';
/**
 * What a firm outside the four gets, and what a setup with no country claims.
 *
 * TWIN: `DEFAULT_NORM_CODE` in `backend/src/kalqa/db/norms.py`. It is `EN-ISO` and not
 * `EN-ISO baseline` — the latter was this file's own invention and names no catalogue row.
 */
export const NORM_EN_ISO = 'EN-ISO';

/**
 * The edition a client should read, or `null` when the norm has none.
 *
 * `current` and not "the newest": the catalogue also carries `draft` versions of laws that
 * have not taken effect and `superseded` ones that projects still run on, and exactly one row
 * per norm is `current` (a partial unique index says so). A norm with no current version is
 * ÖNORM — real, in the list on purpose, and refused by `PUT /workspace/norms` with a `422`
 * naming it, which is why the wizard and the settings screen both draw it disabled rather
 * than letting somebody find out at save time.
 */
export function currentVersionOf(norm: Norm): NormVersion | null {
  return norm.versions?.find((version) => version.status === 'current') ?? null;
}

/** Whether this norm can be enabled at all. See `currentVersionOf` for why it can fail. */
export function isSelectable(norm: Norm): boolean {
  return currentVersionOf(norm) !== null;
}

export interface CountryPreset {
  /** Stable key for React and for the selected-country comparison. */
  readonly id: string;
  /**
   * ISO 3166-1 alpha-2, or `null` for the generic option. `null` is sent as *no* `country`
   * key at all rather than as an empty string — the schema wants exactly two characters or
   * nothing (`min_length=2, max_length=2`).
   */
  readonly country: string | null;
  readonly flag: string;
  /** What the mono code column prints. `country.XX.code` is an em dash for the generic row. */
  readonly codeKey: SetupKey | null;
  readonly nameKey: SetupKey;
  readonly metaKey: SetupKey;
  /** Seeded into the draft on pick. Both stay editable afterwards. */
  readonly locale: string;
  /** The `norm.code` this country ticks, and the default it starts on. */
  readonly normCode: string;
  /** The mock's "Common" pill. Marks the country most new firms turn out to be in. */
  readonly common?: boolean;
}

/**
 * The bundled preset. These four are one group because they share the EN-ISO base, A-series
 * paper, metric units and a date format — the reasoning is in MCK-20260521-010's
 * "Why SK/CZ/AT bundled".
 */
export const CENTRAL_EUROPE: readonly CountryPreset[] = [
  {
    id: 'SK',
    country: 'SK',
    flag: '🇸🇰',
    codeKey: null,
    nameKey: 'country.SK.name',
    metaKey: 'country.SK.meta',
    locale: 'sk',
    normCode: NORM_STN,
  },
  {
    id: 'CZ',
    country: 'CZ',
    flag: '🇨🇿',
    codeKey: null,
    nameKey: 'country.CZ.name',
    metaKey: 'country.CZ.meta',
    locale: 'cs',
    normCode: NORM_CSN,
  },
  {
    id: 'AT',
    country: 'AT',
    flag: '🇦🇹',
    codeKey: null,
    nameKey: 'country.AT.name',
    metaKey: 'country.AT.meta',
    locale: 'de',
    // **Austria seeds `DIN`, not `ÖNORM`, and this is not a shrug.** `ÖNORM` is in the
    // catalogue with no version seeded, because its ladder has not been read from primary
    // source — so ticking it would hand an Austrian studio a workspace it cannot start a
    // project in. Austrian practice runs an analogous Leistungsphasen structure
    // (`MCK-20260812-011` §Notes), which is the ladder `DIN` carries. The `…meta` line names
    // HOAI for the same reason: the card must not promise a standard the tick does not send.
    // TWIN: `_BY_COUNTRY` in `domains/workspaces/service.py` maps `AT -> DIN` identically.
    normCode: NORM_DIN,
    common: true,
  },
  {
    id: 'DE',
    country: 'DE',
    flag: '🇩🇪',
    codeKey: null,
    nameKey: 'country.DE.name',
    metaKey: 'country.DE.meta',
    locale: 'de',
    normCode: NORM_DIN,
  },
];

/**
 * Everywhere else. All of them seed the generic baseline rather than their own national
 * body: the catalogue holds five norms and MSZ, PN, SIA, BS and ANSI are not among them, so
 * the meta line names the local standard the office will recognise while the *tick* stays a
 * value the product actually implements. Nothing is locked — a Warsaw practice that takes
 * German work ticks `DIN` beside it, here or later in Studio settings.
 */
export const OTHER_REGIONS: readonly CountryPreset[] = [
  {
    id: 'HU',
    country: 'HU',
    flag: '🇭🇺',
    codeKey: null,
    nameKey: 'country.HU.name',
    metaKey: 'country.HU.meta',
    locale: 'hu',
    normCode: NORM_EN_ISO,
  },
  {
    id: 'PL',
    country: 'PL',
    flag: '🇵🇱',
    codeKey: null,
    nameKey: 'country.PL.name',
    metaKey: 'country.PL.meta',
    locale: 'pl',
    normCode: NORM_EN_ISO,
  },
  {
    id: 'CH',
    country: 'CH',
    flag: '🇨🇭',
    codeKey: null,
    nameKey: 'country.CH.name',
    metaKey: 'country.CH.meta',
    locale: 'de',
    normCode: NORM_EN_ISO,
  },
  {
    id: 'GB',
    country: 'GB',
    flag: '🇬🇧',
    codeKey: null,
    nameKey: 'country.GB.name',
    metaKey: 'country.GB.meta',
    locale: 'en',
    normCode: NORM_EN_ISO,
  },
  {
    id: 'US',
    country: 'US',
    flag: '🇺🇸',
    codeKey: null,
    nameKey: 'country.US.name',
    metaKey: 'country.US.meta',
    locale: 'en',
    normCode: NORM_EN_ISO,
  },
  {
    id: 'XX',
    country: null,
    flag: '🌐',
    codeKey: 'country.XX.code',
    nameKey: 'country.XX.name',
    metaKey: 'country.XX.meta',
    locale: 'en',
    normCode: NORM_EN_ISO,
  },
];

export const ALL_COUNTRIES: readonly CountryPreset[] = [...CENTRAL_EUROPE, ...OTHER_REGIONS];

/** The country whose `country` code matches, or `undefined` — used to restore a prefill. */
export function countryByCode(code: string | null | undefined): CountryPreset | undefined {
  if (code === null || code === undefined) return undefined;
  const wanted = code.toUpperCase();
  return ALL_COUNTRIES.find((candidate) => candidate.country === wanted);
}

/**
 * The country the browser's language tags name, or `undefined` — the country card's default,
 * the same way the language chip defaults to the browser's language (`MCK-20260521-010`,
 * owner 2026-09-12; research 29 J2-5).
 *
 * Only a tag's **region** counts: `sk-SK` is Slovakia, `de-AT` Austria, `de-DE` Germany. A bare
 * `sk` or `de` names a language, not a country — German alone is spoken in three of the cards —
 * so it seeds nothing, and neither does a region with no card. Tags are read in the browser's
 * preference order and the first one that names a card wins. A malformed tag is skipped.
 */
export function countryFromLanguages(tags: readonly string[]): CountryPreset | undefined {
  for (const tag of tags) {
    let region: string | undefined;
    try {
      region = new Intl.Locale(tag).region;
    } catch {
      continue;
    }
    const preset = countryByCode(region);
    if (preset !== undefined) return preset;
  }
  return undefined;
}

export interface CompanyLanguage {
  /** BCP 47, as `locale` travels on the wire. */
  readonly value: string;
  /**
   * The endonym. Not a catalog entry on purpose: "Slovenčina" is spelled that way on a
   * German page too, which is what an endonym is for — and it is how somebody who cannot
   * read the surrounding language finds their own.
   */
  readonly label: string;
}

/**
 * The company language, which is **not** the reader's interface language. This one seeds
 * document defaults, generated mail and invitations (MCK-20260521-010's three-layer model,
 * workspace layer); the interface language is the person's own and lives in their profile —
 * `components/LanguageSwitcher` is that one.
 *
 * Six here against the four in `lib/i18n/locales.ts`, and that is not a mistake: Hungarian
 * and Polish have no catalog and cannot be a *UI* language, but a Budapest practice whose
 * documents go out in Magyar is a workspace we can serve today.
 */
export const COMPANY_LANGUAGES: readonly CompanyLanguage[] = [
  { value: 'sk', label: 'Slovenčina' },
  { value: 'cs', label: 'Čeština' },
  { value: 'de', label: 'Deutsch' },
  { value: 'en', label: 'English' },
  { value: 'hu', label: 'Magyar' },
  { value: 'pl', label: 'Polski' },
];

/**
 * The generic baseline: no country at all, English, `EN-ISO`. Since `KLQ-20261023-009` there
 * is no "Skip — use EN-ISO defaults" button; `SetupPage` falls back to this norm only when no
 * country is known, never beside a country that has a national norm.
 *
 * **The claim names a code and need not wait for the catalogue**, which is why it takes codes
 * where every later norm request takes ids: it is the one write that may be made before the
 * client has read the catalogue.
 */
export const EN_ISO_DEFAULTS = {
  country: null,
  locale: 'en',
  normCode: NORM_EN_ISO,
} as const;
